import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasActiveSubscription } from '../../utils/stripeService';
import NotificationDropdown from './NotificationDropdown';
import FriendsDropdown from './FriendsDropdown';
import {
  Search,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Crown,
  GamepadIcon,
  Shield
} from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  // Check subscription status when user or userProfile changes
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        console.log('🔧 Header: No user, setting hasSubscription to false');
        setHasSubscription(false);
        setSubscriptionLoading(false);
        return;
      }

      try {
        console.log('🔧 Header: Checking subscription for user:', user.id);
        console.log('🔧 Header: User profile role:', userProfile?.role);
        setSubscriptionLoading(true);
        const active = await hasActiveSubscription();
        console.log('🔧 Header: hasActiveSubscription returned:', active);
        setHasSubscription(active);
      } catch (error) {
        console.error('🔧 Header: Error checking subscription:', error);
        setHasSubscription(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [user, userProfile]);

  // Determine if user should be considered premium (has subscription OR is admin)
  const isPremiumUser = () => {
    if (!user || !userProfile) return false;
    return hasSubscription || userProfile.role === 'admin';
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/user-login');
    } catch (error) {
      console.log('Sign out error:', error);
    }
  };

  const handleSearch = (term) => {
    if (term.trim()) {
      navigate(`/search?q=${encodeURIComponent(term.trim())}`);
    }
  };

  const handleDesktopSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(searchTerm);
  };

  const handleDesktopSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(searchTerm);
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(mobileSearchTerm);
    setIsMenuOpen(false);
  };

  const handleMobileSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(mobileSearchTerm);
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/images/solis-games-logo.png"
              alt="Solis Games"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/browse"
              className="text-foreground hover:text-primary transition-colors"
            >
              Games
            </Link>
            <Link
              to="/library"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              My Library
            </Link>
            <Link
              to="/merch"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              Merch
            </Link>
          </nav>

          {/* Search Bar */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Search WebGPU games, racing, action..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleDesktopSearchKeyPress}
                className="w-64 pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Notifications */}
                <NotificationDropdown />

                {/* Friends */}
                <FriendsDropdown />

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div className="hidden md:flex flex-col items-start">
                      <span className="text-sm font-medium">
                        {userProfile?.full_name || 'User'}
                      </span>
                      {!subscriptionLoading && isPremiumUser() && (
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Crown className="w-3 h-3 mr-1" />
                          {userProfile?.role === 'admin' ? 'Admin' : 'Premium'}
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                      <Link
                        to="/user-profile-settings"
                        className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Profile Settings
                      </Link>
                      <Link
                        to="/subscription-management"
                        className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Subscription
                      </Link>
                      {userProfile?.role === 'studio_admin' && (
                        <Link
                          to="/studio-dashboard"
                          className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <GamepadIcon className="w-4 h-4 mr-2" />
                          Studio Admin
                        </Link>
                      )}
                      {userProfile?.role === 'admin' && (
                        <Link
                          to="/admin-dashboard"
                          className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Link>
                      )}
                      <hr className="my-1 border-border" />
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-muted/50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/user-login"
                  className="px-4 py-2 text-sm text-foreground hover:text-primary transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/user-registration"
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="space-y-2">
              <Link
                to="/browse"
                className="block px-4 py-2 text-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Games
              </Link>
              <Link
                to="/library"
                className="block px-4 py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                My Library
              </Link>
              <Link
                to="/merch"
                className="block px-4 py-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Merch
              </Link>
            </nav>

            {/* Mobile Search */}
            <div className="mt-4 px-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search games..."
                  value={mobileSearchTerm}
                  onChange={(e) => setMobileSearchTerm(e.target.value)}
                  onKeyPress={handleMobileSearchKeyPress}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
